import { ROUTES } from "@repo/core/routes";
import { useProposalFlowNavigation } from "../../utils/proposalFlowNavigation";
import { useFidelityProposalStore } from "../store";
import { useFidelityProposal } from "../hooks/useFidelityProposal";
import { useMemo, useState } from "react";
import { mapToReviewFidelityProposalData } from "./mappers";
import { ConsultProposalResponseDTO } from "@repo/core/services/entities/Proposals";
import useToast from "@repo/core/components/core/Toast";
import { getProposalEffectuationErrorMessage } from "@repo/core/screens/Proposals/rules/proposalEffectuationError";
import { useSelectedProposalServicePoint } from "@repo/core/hooks/useProposalServicePoints";

export const ReviewFidelityProposalController = () => {
  const { replace, back, goToPreviousStep, openReviewEdit } =
    useProposalFlowNavigation();
  const toast = useToast();

  // Use store directly to ensure reactivity
  const proposalData = useFidelityProposalStore((state) => state.proposalData);
  const resetFidelityProposalStore = useFidelityProposalStore(
    (state) => state.resetFidelityProposalStore
  );

  const { effectuateFidelityProposal } = useFidelityProposal();
  const { selectedServicePoint } =
    useSelectedProposalServicePoint(proposalData);

  const [isSaving, setIsSaving] = useState(false);

  // Show loading if proposal data is not loaded yet
  const isLoading = !proposalData;

  // Map proposal data to formatted display values
  // Uses strProfissaoDescricao from store if available (saved when profession was selected)
  const formattedData = useMemo(
    () =>
      mapToReviewFidelityProposalData(
        proposalData as ConsultProposalResponseDTO & {
          strProfissaoDescricao?: string;
        }
      ),
    [proposalData]
  );

  const handleGoBack = () => {
    goToPreviousStep(ROUTES.fidelityProposalCompleteRegistration);
  };

  const handleAbandon = () => {
    if (proposalData?.lngProposta) {
      resetFidelityProposalStore();
      replace(ROUTES.proposalsViewInfo, {
        proposalId: proposalData.lngProposta
      });
    } else {
      back();
    }
  };

  const handleClose = () => {
    resetFidelityProposalStore();
    back();
  };

  const handleSubmit = async () => {
    if (isSaving) return;

    try {
      setIsSaving(true);
      const response = await effectuateFidelityProposal();

      if (response.status !== 200) {
        toast.error(
          "Erro ao efetivar proposta",
          getProposalEffectuationErrorMessage(response)
        );
        return;
      }

      resetFidelityProposalStore();
      replace(ROUTES.proposalEffectiveSuccess, {
        proposalId: proposalData?.lngProposta,
        isCreditProposal: false
      });
    } catch (error) {
      toast.error(
        "Erro ao efetivar proposta",
        getProposalEffectuationErrorMessage(error)
      );
      console.error("Error effectuating fidelity proposal:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCompleteRegistration = () => {
    openReviewEdit(ROUTES.fidelityProposalCompleteRegistration);
  };

  const handleEditSelectProduct = () => {
    openReviewEdit(ROUTES.creditProposalSelectProduct);
  };

  const handleEditSelectServicePoint = () => {
    openReviewEdit(ROUTES.creditProposalSelectServicePoint, {
      isFidelityProposal: true
    });
  };

  return {
    handleGoBack,
    handleSubmit,
    handleEditCompleteRegistration,
    handleEditSelectProduct,
    handleEditSelectServicePoint,
    handleClose,
    handleAbandon,
    proposalData,
    selectedServicePoint,
    isLoading,
    formattedData,
    isSaving
  };
};
